import {
  CALVIN_DC_ID,
  HttpStatusCodes,
  networkErrorMessage as networkErrors,
  KEVLAR_IOS_MIN_APP_VERSION,
  KEVLAR_ANDROID_MIN_APP_VERSION
} from '../constants';
import {
  getAuthParams,
  getMAPIDataCenterID,
  getRefreshAT
} from '../utils/bridgeCaller';
import { IAdapterOptions, IMakeNetworkRequest } from './types';
import UAHelper from '../utils/uaHelper';
import { redirectToLoginPage } from '../utils/redirectToLoginPage';
import EnvHelper from '../utils/appEnvironmentHelper';
import {
  shouldTurnOffTenant,
  showDegradedExperience
} from '../utils/appConfigHelper';
import {
  addCustomAttributeToNR,
  decodeATToken,
  extractDeviceID,
  trackDCSwitchIssueOnFailure,
  trackRequestFailure,
  trackSNAndATDistribution
} from './utils';

const {
  AAPI_KEVLAR_REFRESH_CODE,
  TOO_MANY_REQUESTS,
  UNASSIGNED_FROM_AAPI_NGINX
} = HttpStatusCodes;
const dcIdRegex = /^\d\./;
const extractDCIdRegex = /^\d(?=\.)/;

/*
  This API Adapter provides basic methods for CRUD operations and handles appending AT/RT token to request headers necessary for Kevlar session service.

  For more details on Kevlar Migration, Refer here https://docs.google.com/document/d/1BZhUV_5MNkoyMyzmoIUF35PkjnaY2Ku7YY0vtj-D0ic/edit
 */

export class APIAdapter {
  // option to set credentials: 'include' in the request
  private includeCredentials: boolean;
  // option to handle DC exception and retry with the new DC Id
  private enableHeliosDCSwitch: boolean;

  // to cache the dcId in memory
  private dcId?: string;

  private appendKelvarHeader: boolean;
  private enableKevlarSessionService: boolean;

  private redirectToLoginOnUnauthorized: boolean;

  private shouldAvoidCache: boolean;

  private populateATPromise?: Promise<void>;
  private at = '';

  private dcIdPromiseFromBridge?: Promise<string>;

  private _dcIdInProps?: string;

  constructor(options: IAdapterOptions = {}) {
    const { dcId, includeCredentials } = options;
    const fkUA = UAHelper.getUserAgent();

    this.enableKevlarSessionService =
      options.enableKevlarSessionService ?? true;

    this.appendKelvarHeader = this.shouldAppendKevlarHeader();

    this.includeCredentials = includeCredentials ?? true;

    this.enableHeliosDCSwitch =
      (Boolean(dcId) || options?.enableHeliosDCSwitch) ?? true;

    if (this.enableHeliosDCSwitch) {
      this._dcIdInProps = dcId;
      this.dcId = dcId ?? CALVIN_DC_ID;

      this.dcIdPromiseFromBridge = getMAPIDataCenterID();
    }

    this.redirectToLoginOnUnauthorized =
      options?.redirectToLoginOnUnauthorized ?? true;

    this.shouldAvoidCache = options?.shouldAvoidCache ?? false;

    addCustomAttributeToNR('platform', UAHelper.getResolvedPlatformString());
    addCustomAttributeToNR('fkUA', fkUA);

    //push device Id for Android & IOS
    if (UAHelper.isAndroid() || UAHelper.isIOS()) {
      addCustomAttributeToNR('deviceId', extractDeviceID(fkUA));
    }

    if (shouldTurnOffTenant()) {
      showDegradedExperience();
      return;
    }

    if (this.enableKevlarSessionService) {
      this.populateATPromise = getAuthParams().then(params => {
        const jwtToken = decodeATToken(params.at);

        if (this.appendKelvarHeader) {
          trackSNAndATDistribution(
            jwtToken?.vs || params.at,
            params.SN,
            'FPG_AT_AND_SN_STATUS_ON_GET_AUTHPARAMS'
          );
        }
        this.at = params.at;
      });
    }
  }

  private shouldAppendKevlarHeader() {
    if (!this.enableKevlarSessionService) {
      return false;
    }

    if (UAHelper.isIOS()) {
      const appVersion = EnvHelper.getAppVersion() || '';
      return EnvHelper.compareAppVersion({
        appVersion,
        baseVersion: KEVLAR_IOS_MIN_APP_VERSION
      });
    }

    if (UAHelper.isAndroid()) {
      const appVersion = EnvHelper.getAppVersion() || '';
      return (
        EnvHelper.compareAppVersion({
          appVersion,
          baseVersion: KEVLAR_ANDROID_MIN_APP_VERSION
        }) && !!(<any>window)?.Android?.getRefreshAT
      );
    }

    return false;
  }

  /**
   * Function to assign default values to requestParams
   *
   * @param {RequestInfo} input object
   * @param {RequestInit} options?
   * @returns Request (a modified request with mandatory params)
   */
  private async assignMandatoryParams(
    input: RequestInfo,
    options?: RequestInit,
    shouldReplaceDCId = false
  ): Promise<Request> {
    try {
      // Call getAuthParams for every API call to get fresh SN
      const authParams = await getAuthParams();

      const { SN, fkUA, SC } = authParams;

      const freshSNValue = window?.appEnvironment?.SN || SN;

      /*
      Important: Deviation from window.fetch

      Default content-type assisgned by fetch is "text/plain;charset=utf-8"
      
      But we try to have a default content type as 'application/json'.

      However, the following scenario sets Content-Type as null
      new Request(url, { method: 'GET'})

      And the following scenario sets 'Content-Type as 'content/plain-text
      new Request(url, { method: 'POST', body: '{name: 'sam'}' })

      Hence we have a default Content-Type at line no: 130-134
      
    */

      if (typeof options === 'object') {
        const headers = new Headers(options.headers);
        headers.set(
          'Content-Type',
          headers.get('Content-Type') || 'application/json'
        );

        options.headers = headers;
      }

      // www.typescriptlang.org/docs/handbook/2/narrowing.html#using-type-predicates
      /* Typescript < v4.4 doesn't honour checking types via instanceof and reusing the cached values(see eg. below)


      For eg:

      const isInputRequest = input instanceof Request;
      const url = isInputRequest ? input.url : input
  
      Hence we pass the input to create a new request object.
    */
      const request = new Request(input, options);

      if (freshSNValue !== SN) {
        //push logs to NR if available
        window?.NREUM?.addPageAction?.('FPG_SN_VALUE_MISMATCH', {
          pageUrl: window.location.href,
          apiURI: request.url
        });
      }
      const originalURL = new URL(request.url);

      const modifiedHeaders = new Headers(request.headers);

      modifiedHeaders.set('SN', freshSNValue);

      if (this.shouldAvoidCache) {
        modifiedHeaders.set('cache-control', 'no-cache');
      }

      if (this.appendKelvarHeader) {
        await this.populateATPromise;
        modifiedHeaders.set('at', this.at);
      }

      if (this.enableKevlarSessionService) {
        modifiedHeaders.set('x-kevlar-enabled', 'true');
      }

      modifiedHeaders.set('X-User-Agent', fkUA);
      modifiedHeaders.set('flipkart_secure', 'true');

      if (SC) {
        modifiedHeaders.set('SC', SC);
        modifiedHeaders.set('secureCookie', SC);
      }

      const body = await request.blob();

      let newURL = originalURL;
      let dcIdAppendedSource = '';

      // check for handleHeliosDCException
      if (this.enableHeliosDCSwitch) {
        let { hostname } = originalURL;

        const { dcId } = this;

        if (dcIdRegex.test(originalURL.hostname)) {
          if (shouldReplaceDCId) {
            dcIdAppendedSource = 'REPLACED_BY_ADAPTER_ON_DC_SWITCH';
            hostname = originalURL.hostname.replace(dcIdRegex, `${dcId}.`);
          } else {
            dcIdAppendedSource = 'PROVIDED_IN_URL';
          }
        } else {
          dcIdAppendedSource = this._dcIdInProps
            ? 'PROVIDED_AS_PROPS_TO_ADAPTER'
            : 'ASSIGNED_BY_ADAPTER_ON_FIRST_CALL';
          hostname = `${dcId}.${originalURL.hostname}`;
        }

        newURL = new URL(
          `${originalURL.protocol}//${hostname}${originalURL.pathname}${originalURL.search}`
        );
      }

      const method = request?.method?.toUpperCase() || 'GET';

      const modifiedRequest = new Request(newURL.toString(), {
        method: request.method,
        body: method !== 'GET' && method !== 'HEAD' ? body : null,
        referrer: request.referrer,
        referrerPolicy: request.referrerPolicy,
        mode: request.mode,
        cache: request.cache,
        redirect: request.redirect,
        integrity: request.integrity,
        keepalive: request.keepalive,
        destination: request.destination,
        credentials: this.includeCredentials ? 'include' : 'omit',
        headers: modifiedHeaders
      } as RequestInit);

      if (dcIdAppendedSource) {
        modifiedRequest.dcIdAppendedSource = dcIdAppendedSource;
      }

      return modifiedRequest;
    } catch (e) {
      // better error for debugging
      const error = new Error(
        //@ts-ignore
        `Error while assigning mandatory params or constructing modified request: \n${e.stack}`
      );
      throw error;
    }
  }

  // Internal method for initiating request
  private async makeNetworkRequest({
    input,
    options,
    shouldReplaceDCId = false
  }: IMakeNetworkRequest): Promise<Response> {
    try {
      let jwtToken;
      const modifiedRequest = await this.assignMandatoryParams(
        input,
        options,
        shouldReplaceDCId
      );

      const clonedRequest = modifiedRequest.clone();

      if (this.enableHeliosDCSwitch) {
        clonedRequest.dcIdAppendedSource = modifiedRequest.dcIdAppendedSource;
      }

      // pass the cloned instance of the request, so the actual request can be reused in case of failure
      const response = await window.fetch(clonedRequest);
      const responseStatus = response.status;
      const isTooManyRequests =
        responseStatus === TOO_MANY_REQUESTS ||
        responseStatus === UNASSIGNED_FROM_AAPI_NGINX;

      if (
        !response.ok ||
        responseStatus === AAPI_KEVLAR_REFRESH_CODE ||
        isTooManyRequests
      ) {
        //Degraded Experience on TOO_MANY_REQUESTS
        if (isTooManyRequests) {
          showDegradedExperience();
          return Promise.reject(new Response('{}'));
        }
        if (
          responseStatus === HttpStatusCodes.DC_SWITCH &&
          this.enableHeliosDCSwitch
        ) {
          // Cloning the response for reading the RESPONSE.id
          const clonedResponse = response.clone();
          const { RESPONSE } = await clonedResponse.json();
          const { id: dcId } = RESPONSE;

          this.dcId = dcId;

          // track dcId appended source for HYD on initial calls
          const clonedUrl = new URL(clonedRequest.url);
          const failedDCId = clonedUrl?.hostname?.match(extractDCIdRegex)?.[0];

          if (failedDCId === '2') {
            const dcIdFromBridge = (await this.dcIdPromiseFromBridge) || '';
            trackDCSwitchIssueOnFailure({
              dcIdAppendedSource: clonedRequest.dcIdAppendedSource || '',
              failedDCId,
              dcIdFromBridge,
              apiURI: clonedRequest.url
            });
          }
          /*
          Important:
          
          Always pass the instance of modifiedRequest.
          Because, once a request is parsed, it can't be reused, new request(input) basically throws an error [Line 136: new(input, options)]
        */
          return this.makeNetworkRequest({
            input: modifiedRequest,
            shouldReplaceDCId: true
          });
        } else if (
          responseStatus === AAPI_KEVLAR_REFRESH_CODE &&
          this.enableKevlarSessionService
        ) {
          const data = await getRefreshAT(0);
          //Update new AT after refresh
          //check if its an object data?.at as the getRefreshAt adoption was in phases
          let at = typeof data === 'object' ? data?.at : data;
          this.at = at;

          const jwtToken = decodeATToken(at);
          trackSNAndATDistribution(
            jwtToken?.vs || at,
            modifiedRequest.headers.get('SN') || '',
            'FPG_AT_AND_SN_STATUS_ON_GET_REFRESH_AT'
          );

          // Refer comment line: 236-239
          return this.makeNetworkRequest({ input: modifiedRequest });
        } else if (
          this.redirectToLoginOnUnauthorized &&
          responseStatus === HttpStatusCodes.UNAUTHORIZED
        ) {
          // Log 401 if its caused due to AT(Logged IN - LI) from bridge method along with SN
          if (this.appendKelvarHeader) {
            const { at } = this;
            const jwtToken = decodeATToken(at);

            trackSNAndATDistribution(
              jwtToken?.vs || at,
              modifiedRequest.headers.get('SN') || '',
              'FPG_AT_AND_SN_STATUS_ON_401'
            );
          }

          redirectToLoginPage();
        }

        // log other failures like 400, 404, 500, CORS etc..
        trackRequestFailure(modifiedRequest, response);

        return Promise.reject(response);
      }

      return response;
    } catch (error) {
      if (error instanceof Error && networkErrors.includes(error?.message)) {
        throw new Error('NetworkError');
      }

      throw error;
    }
  }

  /**
   * Function similar to window.fetch
   *
   * @param {RequestInfo} request|url - API URI - similar to fetch API
   * @param {RequestInit} requestParams - options to be based to fetch API
   * @returns Promise
   */

  public async fetch(
    input: RequestInfo,
    options?: RequestInit
  ): Promise<Response> {
    // Turning off the tenant completely
    if (shouldTurnOffTenant()) {
      showDegradedExperience();

      return Promise.reject(new Response('{}'));
    }
    return this.makeNetworkRequest({ input, options });
  }
}

export default APIAdapter;
